import type { NormalizedRect } from '../types/geometry.js';
import { roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import {
  buildSubjectMask,
  clippedRatio,
  collectLuma,
  faceSkinEllipse,
  isInsideSubject,
  lumaStats,
  skinRegions,
  type SkinRegionId,
} from './regions.js';

/**
 * Highlight / blown-out-area analysis.
 *
 * Skin and background are measured *separately* on purpose. A document photo is
 * normally taken against a white wall, so the background legitimately sits at
 * 250+ and counting it as a blown highlight used to reject correct photos. Only
 * clipped *skin* is a defect; the background ratio is reported for the details
 * view and never drives the verdict.
 */

export interface HighlightRegion {
  id: SkinRegionId;
  rect: NormalizedRect;
  /** Share of the region's pixels at/above the near-white threshold. */
  clippedRatio: number;
  meanLuma: number;
  samples: number;
}

export interface HighlightAnalysis {
  /** Share of skin pixels that are clipped — the value the verdict uses. */
  skinHighlightRatio: number;
  skinSamples: number;
  /** Share of background pixels that are near-white — informational only. */
  backgroundWhiteRatio: number;
  backgroundSamples: number;
  clippedLuma: number;
  roiMeans: Record<string, number>;
  regions: HighlightRegion[];
  /** Face box width in analysis-buffer pixels; drives the "too small" fallback. */
  facePixelWidth: number;
}

export interface HighlightOptions {
  /** Luma at/above which a pixel counts as clipped. */
  nearWhiteLuma?: number;
}

export const analyzeHighlights = (input: {
  buffer: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
  options?: HighlightOptions;
}): HighlightAnalysis => {
  const nearWhiteLuma = input.options?.nearWhiteLuma ?? 248;
  const { buffer, geometry } = input;

  const regions: HighlightRegion[] = [];
  const roiMeans: Record<string, number> = {};
  let clippedSkinSamples = 0;
  let skinSamples = 0;

  /** Skin only: the face oval, so the white wall in the box corners is excluded. */
  const skin = faceSkinEllipse(geometry);
  for (const region of skinRegions(geometry)) {
    const values = collectLuma(buffer.luma, buffer.width, buffer.height, region.rect, {
      clipEllipse: skin,
    });
    const stats = lumaStats(values);
    const ratio = clippedRatio(values, nearWhiteLuma);
    clippedSkinSamples += ratio * values.length;
    skinSamples += values.length;
    roiMeans[region.id] = roundTo(stats.mean, 1);
    regions.push({
      id: region.id,
      rect: region.rect,
      clippedRatio: roundTo(ratio, 4),
      meanLuma: roundTo(stats.mean, 1),
      samples: values.length,
    });
  }

  const mask = buildSubjectMask({ geometry, subjectRect: geometry.faceBox });
  const step = Math.max(1, Math.floor(Math.min(buffer.width, buffer.height) / 240));
  let backgroundSamples = 0;
  let backgroundWhite = 0;
  for (let y = 0; y < buffer.height; y += step) {
    for (let x = 0; x < buffer.width; x += step) {
      const nx = (x + 0.5) / buffer.width;
      const ny = (y + 0.5) / buffer.height;
      if (isInsideSubject(mask, nx, ny)) continue;
      backgroundSamples += 1;
      if ((buffer.luma[y * buffer.width + x] as number) >= nearWhiteLuma) backgroundWhite += 1;
    }
  }

  return {
    skinHighlightRatio: skinSamples > 0 ? clippedSkinSamples / skinSamples : 0,
    skinSamples,
    backgroundWhiteRatio: backgroundSamples > 0 ? backgroundWhite / backgroundSamples : 0,
    backgroundSamples,
    clippedLuma: nearWhiteLuma,
    roiMeans,
    regions,
    facePixelWidth: geometry.faceBox.width * buffer.width,
  };
};
